import { defineStore } from "pinia";

import type {
  AccountType,
  AccountAddresses,
  AccountPaymentMethods,
} from "@/types";

interface State {
  account: AccountType | null;
  loading: boolean;
}

export const useAccountStore = defineStore({
  id: "accountStore",
  state: (): State => ({
    account: null,
    loading: false,
  }),

  // Add persistence to maintain account data across browser refreshes
  persist: process.client
    ? ({
        storage: localStorage,
        key: "accountStore",
        paths: ["account.addresses", "account.paymentMethods"],
      } as any)
    : false,

  actions: {
    clearAccount() {
      this.account = {
        // Core identification fields
        id: undefined,
        customerId: undefined,
        username: undefined,
        documentId: undefined,

        // Authentication and status fields
        emailVerified: false,
        identityVerified: false,
        signedIn: false,
        confirmed: false,
        blocked: false,
        siteActiveId: undefined,

        // Email and contact info
        emailPrimary: "",
        invoiceEmailAddress: "",
        confirmEmail: "",

        // Personal details
        salutation: "",
        firstName: "",
        lastName: "",
        dateOfBirth: "",

        // Account actions & preferences
        password: "",
        confirmPassword: "",
        acceptTerms: false,
        locale: undefined,

        // Timestamps
        createdAt: undefined,
        updatedAt: undefined,
        publishedAt: undefined,

        // Associated data collections
        addresses: [],
        paymentMethods: [],
        isNewCustomer: false,
      };
      this.loading = false;
    },
    updateAccount(account: AccountType) {
      this.account = account;
    },
    addAddress(address: AccountAddresses) {
      if (this.account && this.account.addresses) {
        // Remove any existing address with id=0 (previous "NEW" addresses) to prevent duplicates
        this.account.addresses = this.account.addresses.filter(
          (existingAddress) => existingAddress.id !== 0
        );

        // Add the new address
        this.account.addresses?.push(address);
      }
    },
    updateAddress(address) {
      if (this.account && this.account.addresses) {
        // Find the index of the address to update
        console.log("🔍 Attempting to update address:", address);
        const index = this.account.addresses.findIndex((existingAddress) => {
          console.log("🔎 Checking existing address:", existingAddress);
          return existingAddress.id === address.id;
        });
        console.log("🔎 Found address index:", index);
        if (index !== -1) {
          console.log("✅ Updating address at index", index, "with", address);
          this.account.addresses[index] = address;
        } else {
          console.warn("⚠️ Address to update not found:", address.id);
        }
      }
    },
    selectDefaultAddress(index: number) {
      if (this.account && this.account.addresses) {
        this.account.addresses.forEach((address) => {
          address.defaultAddress = false;
        });
        this.account.addresses[index].defaultAddress = true;
      }
    },
    selectDefaultPaymentMethod(index: number) {
      if (this.account && this.account.paymentMethods) {
        for (let i = 0; i < this.account.paymentMethods.length; i++) {
          this.account.paymentMethods[i].default = false;
        }
        this.account.paymentMethods[index].default = true;
      }
    },
    addPaymentMethod(paymentMethod: AccountPaymentMethods) {
      if (this.account) {
        if (!this.account.paymentMethods) {
          this.account.paymentMethods = [];
        }
        this.account.paymentMethods.push(paymentMethod);
      }
    },
    addPaymentMethodWithNewId(paymentMethod: AccountPaymentMethods) {
      if (this.account) {
        if (!this.account.paymentMethods) {
          this.account.paymentMethods = [];
        }
        // Remove any existing payment method with id=0 (previous "NEW" payment methods) to prevent duplicates
        this.account.paymentMethods = this.account.paymentMethods.filter(
          (existingPaymentMethod) => existingPaymentMethod.id !== 0
        );

        // Add the new payment method with id=0 to mark it as NEW
        const newPaymentMethod = {
          ...paymentMethod,
          id: 0,
        };
        this.account.paymentMethods.push(newPaymentMethod);
        console.log(
          "💳 Added NEW payment method to account store with id=0:",
          newPaymentMethod
        );
      }
    },
    setIsNewCustomer(isNewCustomer: boolean) {
      if (this.account) {
        this.account.isNewCustomer = isNewCustomer;
      }
    },
    removeNewAddresses() {
      if (this.account && this.account.addresses) {
        // Remove any addresses with id=0 (NEW addresses) after successful order submission
        this.account.addresses = this.account.addresses.filter(
          (address) => address.id !== 0
        );
        console.log(
          "🧹 Cleanup: Removed NEW addresses from account store after successful order submission"
        );
      }
    },
    removeNewPaymentMethods() {
      if (this.account && this.account.paymentMethods) {
        // Remove any payment methods with id=0 (NEW payment methods) after successful order submission
        this.account.paymentMethods = this.account.paymentMethods.filter(
          (paymentMethod) => paymentMethod.id !== 0
        );
        console.log(
          "🧹 Cleanup: Removed NEW payment methods from account store after successful order submission"
        );
      }
    },
  },
  getters: {},
});
